import { MouseEvent, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { Button, Divider, Tooltip } from 'src/components/atoms';
import { StyledIconInButton } from 'src/components/foundations/foundations.styles';
import {
  ButtonAndTooltip,
  InformationBanner,
  UserInformationPanel,
} from 'src/components/molecules';
import { casePageHeader } from 'src/components/pages/Case/Case.slice';
import { useGetCaseLinksByCaseIdQuery } from 'src/graphql';
import { useIsFeatureEnabled } from 'src/hooks';
import { CaseTypeEnum, FeatureFlag, FormState } from 'src/types';
import { useTheme } from 'styled-components';
import { CaseStatusAndCategoriesPanel } from '../../CaseStatusAndCategoriesPanel';
import { LinkCaseModal } from '../../LinkCaseModal';
import { CaseDatesPanel } from '../CaseDatesPanel';
import { CaseLinksPanel } from '../CaseLinksPanel';
import { CaseTeamPanel } from '../CaseTeamPanel';
import {
  CaseIdContainer,
  CaseIdTitle,
  CaseScreenHeaderDetailsNew,
  DividerContainer,
  ImportedButtonTag,
  ImportedButtonText,
  InformationBannerContainer,
  NamedOffenderListContainer,
  OriginalCaseIdText,
  StyledHeaderAccordion,
} from './CasePageHeader.styles';

export interface CasePageHeaderProps {
  submissionRoute?: string;
  caseType: CaseTypeEnum;
}

export const CasePageHeader = ({
  submissionRoute,
  caseType,
}: CasePageHeaderProps) => {
  const theme = useTheme();
  const [linkCaseModalOpen, setLinkCaseModalOpen] = useState(false);
  const {
    caseReadableId,
    namedOffenders,
    enableNamedOffenders,
    canEditCaseDetails,
    caseId,
    showCaseLinksSection,
    isImportedCase,
    importedCaseOriginalId,
    isCaseSanitized,
  } = useSelector(casePageHeader);
  const { refetch: refetchCaseLinks } = useGetCaseLinksByCaseIdQuery(
    { caseId },
    { enabled: false, retry: false },
  );
  const [root, setRoot] = useState<HTMLElement | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const linkingCasesEnabled = useIsFeatureEnabled(FeatureFlag.LINKING_CASES);

  const openLinkCaseModal = (e: MouseEvent) => {
    e.stopPropagation();
    setLinkCaseModalOpen(true);
  };
  const canLinkCases =
    caseType === CaseTypeEnum.SPEAK_UP &&
    canEditCaseDetails &&
    linkingCasesEnabled;
  const canShowCaseLinksSection =
    caseType === CaseTypeEnum.SPEAK_UP &&
    linkingCasesEnabled &&
    showCaseLinksSection;

  const namedOffendersWithId =
    isCaseSanitized && namedOffenders.filter((o) => o.name).length === 0
      ? [
          {
            id: 0,
            name: 'SANITIZED',
            description: '',
            badgeText: '[SANITIZED]',
          },
        ]
      : namedOffenders.map((offender) => ({
          ...offender,
          id: parseInt(offender.id, 10),
          description: offender.reportOrEvent,
        }));

  return (
    <>
      <StyledHeaderAccordion
        defaultOpen
        data-testid="case-screen-header-accordion"
        title={
          <CaseIdContainer>
            <CaseIdTitle data-testid="report-readable-id">{`CASE ID: ${caseReadableId}`}</CaseIdTitle>
            {isImportedCase && (
              <ImportedButtonTag variant="secondary" size="small">
                <StyledIconInButton
                  size="small"
                  className="uil uil-file-download"
                  position="left"
                  customStyle={{ paddingRight: '0' }}
                />
                <ImportedButtonText>Imported</ImportedButtonText>
                {importedCaseOriginalId && (
                  <>
                    |
                    <OriginalCaseIdText
                      onMouseOver={() => setTooltipVisible(true)}
                      onMouseOut={() => setTooltipVisible(false)}
                      ref={setRoot}
                    >
                      Original Case ID: {importedCaseOriginalId}
                    </OriginalCaseIdText>
                    {tooltipVisible &&
                      root &&
                      importedCaseOriginalId.length > 10 &&
                      createPortal(
                        <Tooltip
                          text={importedCaseOriginalId}
                          variant="left"
                          root={root}
                          lineBreak="anywhere"
                        />,
                        document.body,
                      )}
                  </>
                )}
              </ImportedButtonTag>
            )}
            {canLinkCases && (
              <Button
                variant="secondary"
                size="small"
                noTextWrap
                data-testid="case-screen-header-link-case-button"
                onClick={openLinkCaseModal}
              >
                <StyledIconInButton
                  size="small"
                  className="uil uil-link"
                  position="left"
                />
                Link case
              </Button>
            )}
          </CaseIdContainer>
        }
        subtitle={
          caseType === CaseTypeEnum.SPEAK_UP ? (
            <ButtonAndTooltip
              disabled={!enableNamedOffenders && !isCaseSanitized}
              shouldUsePortal
              buttonProps={{
                variant: 'tertiary',
                size: 'small',
                children: (
                  <>
                    <StyledIconInButton
                      className="uil uil-user"
                      size="small"
                      position="left"
                    />
                    Named offender
                  </>
                ),
              }}
              tooltipProps={{
                variant: 'up',
                width: '470px',
                removePadding: true,
                children: (
                  <NamedOffenderListContainer>
                    <UserInformationPanel
                      users={namedOffendersWithId}
                      badgeWidth="160px"
                    />
                  </NamedOffenderListContainer>
                ),
              }}
            />
          ) : undefined
        }
        customCollapseIconOpen={
          <Button
            variant="tertiary"
            size="small"
            noTextWrap
            data-testid="case-screen-header-accordion-collapse-icon-open"
          >
            <StyledIconInButton
              size="small"
              className="uil uil-eye-slash"
              position="left"
            />
            Hide case details
          </Button>
        }
        customCollapseIconClosed={
          <Button
            variant="tertiary"
            size="small"
            noTextWrap
            data-testid="case-screen-header-accordion-collapse-icon-closed"
          >
            <StyledIconInButton
              size="small"
              className="uil uil-eye-slash"
              position="left"
            />
            Show case details
          </Button>
        }
        topBorderRadius="12px"
        bottomBorderRadius="12px"
        topBorder
        bottomBorder={false}
        last
      >
        <DividerContainer>
          <Divider
            variant="horizontal"
            color={
              theme.isVaultVariant
                ? theme.newPalette.neutral.N300
                : theme.atlas.semantic.color.ui.divider.default
            }
          />
        </DividerContainer>

        {isImportedCase && (
          <InformationBannerContainer>
            <InformationBanner
              state={FormState.INFO}
              icon="uil uil-file-download"
              title="Imported case"
              description="This case was imported from a legacy system. Some fields have been mapped from the original data."
            />
          </InformationBannerContainer>
        )}

        <CaseScreenHeaderDetailsNew>
          <CaseTeamPanel />

          <div>
            <Divider
              variant="vertical"
              color={
                theme.isVaultVariant
                  ? theme.newPalette.neutral.N300
                  : theme.atlas.semantic.color.ui.divider.default
              }
            />
          </div>

          <CaseStatusAndCategoriesPanel
            caseType={caseType}
            submissionRoute={submissionRoute}
          />

          <CaseDatesPanel />
        </CaseScreenHeaderDetailsNew>

        {canShowCaseLinksSection && (
          <>
            <DividerContainer>
              <Divider
                variant="horizontal"
                color={
                  theme.isVaultVariant
                    ? theme.newPalette.neutral.N300
                    : theme.atlas.semantic.color.ui.divider.default
                }
              />
            </DividerContainer>

            <CaseLinksPanel
              openLinkCaseModal={openLinkCaseModal}
              refetchCaseLinks={refetchCaseLinks}
            />
          </>
        )}
      </StyledHeaderAccordion>
      {canLinkCases && (
        <LinkCaseModal
          caseId={caseId}
          readableId={caseReadableId}
          open={linkCaseModalOpen}
          setOpen={setLinkCaseModalOpen}
          refetchCaseLinks={refetchCaseLinks}
        />
      )}
    </>
  );
};
